import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ProcessListItem } from '../data/process-list-item';
import { Process } from '../data/process';
import { BodyProcess } from '../body/body-process';
import { BodyApproval } from '../body/body-approval';
import { Observable, Subject } from 'rxjs';
import { BodyProcessStatus } from '../body/body-process-status';
import { ResponseNumber } from '../data/number';
import { BodyResubmit } from '../body/body-resubmit';
import { BodyProcessAttachment } from '../body/body-process-attachment';

@Injectable({
  providedIn: 'root'
})
export class ProcessService {
  private http = inject(HttpClient);

  controllerURL = '/api/processes';

  subject = new Subject<unknown>();

  getToApproveObservable(): Observable<unknown> {
    return this.subject.asObservable();
  }

  getMyProcesses(from: string | null, to: string | null): Observable<ProcessListItem[]> {
    let params = new HttpParams();
    if (from) {
      params = params.append('from', from);
    }
    if (to) {
      params = params.append('to', to);
    }
    return this.http.get<ProcessListItem[]>(environment.apiUrl + this.controllerURL + '/my', { params });
  }

  getProcessesToApprove(): Observable<ProcessListItem[]> {
    return this.http.get<ProcessListItem[]>(environment.apiUrl + this.controllerURL + '/to-approve');
  }

  countProcessesToApprove(): Observable<ResponseNumber> {
    return this.http.get<ResponseNumber>(environment.apiUrl + this.controllerURL + '/count-to-approve');
  }

  getProcessesOpenForProcessing(): Observable<ProcessListItem[]> {
    return this.http.get<ProcessListItem[]>(environment.apiUrl + this.controllerURL + '/open-for-operators');
  }

  getProcessesToProcessing(from: string | null, to: string | null): Observable<ProcessListItem[]> {
    let params = new HttpParams();
    if (from) {
      params = params.append('from', from);
    }
    if (to) {
      params = params.append('to', to);
    }
    return this.http.get<ProcessListItem[]>(environment.apiUrl + this.controllerURL + '/for-operators', { params });
  }

  countProcessesToProcessing(): Observable<ResponseNumber> {
    return this.http.get<ResponseNumber>(environment.apiUrl + this.controllerURL + '/count-open-for-operators');
  }

  getOldProcessesAsApprover(from: string | null, to: string | null): Observable<ProcessListItem[]> {
    let params = new HttpParams();
    if (from) {
      params = params.append('from', from);
    }
    if (to) {
      params = params.append('to', to);
    }
    return this.http.get<ProcessListItem[]>(environment.apiUrl + this.controllerURL + '/old-as-approver', { params });
  }

  getProcess(id: string): Observable<Process> {
    return this.http.get<Process>(environment.apiUrl + this.controllerURL + '/' + id);
  }

  createProcess(body: BodyProcess, notify: boolean): Observable<string> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    const params = new HttpParams()
      .append('notify', notify.toString());

    return this.http.post<string>(environment.apiUrl + this.controllerURL, body, { headers, params });
  }

  updateProcess(body: BodyProcess, id: string, notify: boolean): Observable<boolean> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    const params = new HttpParams()
      .append('notify', notify.toString());

    return this.http.put<boolean>(environment.apiUrl + this.controllerURL + '/' + id, body, { headers, params });
  }

  cancelProcess(id: string): Observable<boolean> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.post<boolean>(environment.apiUrl + this.controllerURL + '/' + id + '/cancel', null, { headers });
  }

  cancelProcessAfterApproved(id: string): Observable<boolean> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.post<boolean>(environment.apiUrl + this.controllerURL + '/' + id + '/cancel-after-approved', null, { headers });
  }

  deleteProcess(id: string): Observable<boolean> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.delete<boolean>(environment.apiUrl + this.controllerURL + '/' + id, { headers });
  }

  approveProcess(body: BodyApproval[], id: string): Observable<void> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.post<void>(environment.apiUrl + this.controllerURL + '/' + id + '/approve', body, { headers });
  }

  resubmitProcess(body: BodyResubmit, id: string): Observable<void> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.post<void>(environment.apiUrl + this.controllerURL + '/' + id + '/resubmit', body, { headers });
  }

  progressProcess(body: BodyProcessStatus, id: string): Observable<void> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.post<void>(environment.apiUrl + this.controllerURL + '/' + id + '/operate', body, { headers });
  }

  createPdf(id: string): Observable<HttpResponse<Blob>> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');
    return this.http.get<Blob>(environment.apiUrl + this.controllerURL + '/' + id + '/pdf', { headers, observe: 'response', responseType: 'blob' as 'json' });
  }

  failTransmission(id: string): Observable<void> {
    return this.http.post<void>(environment.apiUrl + this.controllerURL + '/' + id + '/fail-transmission', null);
  }

  exportMyProcesses(formId: string, from: string, to: string): Observable<HttpResponse<Blob>> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    const params = new HttpParams()
      .append('formId', formId)
      .append('from', from)
      .append('to', to);

    return this.http.get<Blob>(environment.apiUrl + this.controllerURL + '/export-my', { headers, params, observe: 'response', responseType: 'blob' as 'json' });
  }

  exportProcessesForApprovers(formId: string, from: string, to: string): Observable<HttpResponse<Blob>> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    const params = new HttpParams()
      .append('formId', formId)
      .append('from', from)
      .append('to', to);

    return this.http.get<Blob>(environment.apiUrl + this.controllerURL + '/export-for-approvers', { headers, params, observe: 'response', responseType: 'blob' as 'json' });
  }

  exportProcessesForOperators(formId: string, from: string, to: string): Observable<HttpResponse<Blob>> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    const params = new HttpParams()
      .append('formId', formId)
      .append('from', from)
      .append('to', to);

    return this.http.get<Blob>(environment.apiUrl + this.controllerURL + '/export-for-operators', { headers, params, observe: 'response', responseType: 'blob' as 'json' });
  }

  createAttachment(body: BodyProcessAttachment, id: string): Observable<void> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json');

    return this.http.post<void>(environment.apiUrl + this.controllerURL + '/' + id + '/attachments', body, { headers });
  }

  getAttachment(processId: string, attachmentId: string): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>(environment.apiUrl + this.controllerURL + '/' + processId + '/attachments/' + attachmentId, { observe: 'response', responseType: 'blob' as 'json' });
  }

  deleteAttachment(processId: string, attachmentId: string): Observable<void> {
    return this.http.delete<void>(environment.apiUrl + this.controllerURL + '/' + processId + '/attachments/' + attachmentId);
  }

  downloadMyProcesses(): void {
    this.getDownloadFile().subscribe(
      (response: HttpResponse<Blob>) => {
        const binaryData: BlobPart[] = [];
        binaryData.push(response.body as BlobPart);
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
        downloadLink.setAttribute('download', 'meineAnträge.zip');
        document.body.appendChild(downloadLink);
        downloadLink.click();
      });
  }

  getDownloadFile(): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>(environment.apiUrl + this.controllerURL + '/download-my-process', {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }
}
